/** The only legacy UI route literals. Redirects replace history and preserve other search keys. */
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { adminApi } from '@/api/tmm/admin';
import { ApiError } from '@/api/errors';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorState, PageHeader } from '@/components/page';
import { useCopy } from '@/hooks/useCopy';
import { useRecent } from '@/components/palette/useRecent';
import { useNotify } from '@/components/page';
import { legacyTab } from './partnerSections';

export const LEGACY_ROUTES = {
  resellerList: '/resellers',
  reseller: '/resellers/:resellerId/*',
  tenantList: '/tenants',
  tenant: '/tenants/:slug/*',
};
export function tenantPartnerPath(slug: string, suffix = '') {
  return `/partners/by-tenant/${encodeURIComponent(slug)}${suffix}`;
}
export function redirectSearch(
  search: string,
  kind: 'tenant' | 'reseller',
  forced?: string,
): string {
  const params = new URLSearchParams(search);
  let tab = forced ?? legacyTab(kind, params.get('tab'));
  // TMMPDM-0100: `focus=notices` named the old region on Standing. It is the notices leaf now.
  if (forced === undefined && params.get('focus') === 'notices' && kind === 'reseller') {
    tab = 'notices';
  }
  params.delete('focus');
  if (tab === 'overview') params.delete('tab');
  else params.set('tab', tab);
  const query = params.toString();
  return query ? `?${query}` : '';
}
export function PartnerListRedirect() {
  const location = useLocation();
  return (
    <Navigate replace to={`/partners${location.search}${location.hash}`} state={location.state} />
  );
}
export function ResellerRedirect() {
  const { resellerId, '*': suffix = '' } = useParams();
  const location = useLocation();
  const child = suffix === 'ghl' ? '' : suffix;
  const search = child
    ? location.search
    : redirectSearch(location.search, 'reseller', suffix === 'ghl' ? 'ghl' : undefined);
  return (
    <Navigate
      replace
      to={`/partners/${resellerId}${child ? `/${child}` : ''}${search}${location.hash}`}
      state={location.state}
    />
  );
}
export function TenantRedirect({ canonical = false }: { canonical?: boolean }) {
  const { slug = '' } = useParams();
  return <TenantRedirectContent key={slug} slug={slug} canonical={canonical} />;
}
function TenantRedirectContent({ slug, canonical }: { slug: string; canonical: boolean }) {
  const { '*': suffix = '' } = useParams();
  const location = useLocation();
  const { t } = useCopy();
  const { forget } = useRecent();
  const notify = useNotify();
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const [token, setToken] = useState(0);
  const [response, setResponse] = useState<{ id: number | null; failure: unknown } | null>(null);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .getTenant(slug)
      .then((tenant) => {
        if (!cancelled) setResponse({ id: tenant.reseller?.id ?? null, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) {
          if (failure instanceof ApiError && failure.status === 404 && forget('tenant', slug))
            notify.info(tRef.current('palette.recentGone', 'That record no longer exists.'));
          setResponse({ id: null, failure });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, token, forget, notify]);
  if (!response) return <LoadingSpinner />;
  if (
    response.failure &&
    !(response.failure instanceof ApiError && response.failure.status === 404)
  ) {
    // A page of its own: the heading and the document title exist for the error too (TMM-B7).
    const title = t('partners.resolveError', 'Could not find this partner.');
    return (
      <Box>
        <PageHeader
          title={title}
          breadcrumbs={[
            { label: t('page.partners.title', 'Partners'), to: '/partners' },
            { label: title },
          ]}
        />
        <ErrorState
          error={response.failure}
          message={title}
          onRetry={() => setToken((n) => n + 1)}
        />
      </Box>
    );
  }
  if (response.id === null)
    return <PageHeader title={t('partners.notFound', 'Partner not found')} />;
  const search = canonical || suffix ? location.search : redirectSearch(location.search, 'tenant');
  return (
    <Navigate
      replace
      to={`/partners/${response.id}${suffix ? `/${suffix}` : ''}${search}${location.hash}`}
      state={location.state}
    />
  );
}
